import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { formatTrackDate } from './utils';

const toUtcInputParts = (value) => {
  if (!value) return { date: '', time: '00:00' };

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return { date: '', time: '00:00' };

  return {
    date: dt.toISOString().slice(0, 10),
    time: dt.toISOString().slice(11, 16),
  };
};

const buildUtcIso = (date, time) => {
  if (!date || !time) return null;
  const dt = new Date(`${date}T${time}:00Z`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
};

const formatLocalFromUtc = (iso) => {
  if (!iso) return null;

  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;

  return {
    full: dt.toLocaleString(), // full local datetime
    date: dt.toLocaleDateString(),
    time: dt.toLocaleTimeString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const ReleaseTrackSummary = ({
  colors,
  releaseType,
  track,
  editable = true,
  updating = false,
  onUpdateScheduledDate,
}) => {
  const enabled = Boolean(track?.enabled);
  const items = Array.isArray(track?.items) ? track.items : [];
  const confirmedCount = items.filter((it) => it?.confirmed).length;
  const status = track?.status || 'draft';

  const locked = ['scheduled', 'releasing', 'released', 'canceled'].includes(status);
  const canEditDate = enabled && editable && !locked;

  const [open, setOpen] = useState(false);

  const initialParts = useMemo(
    () => toUtcInputParts(track?.scheduled_release_date_utc),
    [track?.scheduled_release_date_utc]
  );

  const [date, setDate] = useState(initialParts.date);
  const [time, setTime] = useState(initialParts.time);

  useEffect(() => {
    if (!open) return;
    setDate(initialParts.date);
    setTime(initialParts.time);
  }, [open, initialParts.date, initialParts.time]);

  const nextIso = buildUtcIso(date, time);
  const local = formatLocalFromUtc(nextIso);
  const currentIso = track?.scheduled_release_date_utc
    ? new Date(track.scheduled_release_date_utc).toISOString()
    : null;

  const hasChanged = Boolean(nextIso && nextIso !== currentIso);

  const handleSave = async () => {
    if (!hasChanged || updating) return;
    await onUpdateScheduledDate?.(releaseType, nextIso);
    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          background: colors.primary[500],
          border: `1px solid ${colors.primary[300]}`,
          mb: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Box>
            <Typography variant="h6" sx={{ color: colors.greenAccent[300], fontWeight: 'bold' }}>
              {releaseType} Release
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                Planned release date: {enabled ? formatTrackDate(track?.scheduled_release_date_utc) : 'Not included'}
              </Typography>

              {canEditDate ? (
                <Tooltip title={`Edit ${releaseType} release date`}>
                  <IconButton
                    size="small"
                    onClick={() => setOpen(true)}
                    sx={{
                      ml: 0.5,
                      width: 28,
                      height: 28,
                      color: colors.greenAccent[200],
                      border: `1px solid ${colors.greenAccent[500]}`,
                      backgroundColor: colors.primary[400],
                      '&:hover': {
                        color: colors.greenAccent[100],
                        borderColor: colors.greenAccent[300],
                        backgroundColor: colors.greenAccent[900],
                      },
                    }}
                  >
                    <EditOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              size="small"
              label={enabled ? 'Included' : 'Not Included'}
              sx={{
                bgcolor: enabled ? colors.greenAccent[700] : colors.redAccent[700],
                color: colors.grey[100],
              }}
            />
            <Chip
              size="small"
              label={`Confirmed: ${confirmedCount}/${items.length}`}
              sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100] }}
            />
            <Chip
              size="small"
              label={`Status: ${status}`}
              sx={{ bgcolor: colors.primary[300], color: colors.grey[100] }}
            />
          </Box>
        </Box>
      </Box>

      <Dialog open={open} onClose={() => (!updating ? setOpen(false) : null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {releaseType} Release Date</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Release Day (UTC)"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              size="small"
              label="Release Time (UTC)"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
            />

            <Box>
              <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
                UTC: {nextIso || 'Invalid date'}
              </Typography>

              {local ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.greenAccent[200],
                    display: 'block',
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Local ({local.tz}): {local.full}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: colors.redAccent[300], display: 'block', mt: 0.5 }}>
                  Invalid local time
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            disabled={updating}
            sx={{
              fontWeight: 800,
              backgroundColor: colors.grey[100],
              color: colors.redAccent[600],
              border: `1px solid ${colors.redAccent[500]}`,
              '&:hover': {
                backgroundColor: colors.grey[200],
                borderColor: colors.redAccent[400],
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={!hasChanged || updating}
            onClick={handleSave}
            sx={{
              fontWeight: 800,
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
              '&:hover': { backgroundColor: colors.greenAccent[400] },
              '&.Mui-disabled': {
                backgroundColor: colors.primary[300],
                color: colors.grey[400],
                opacity: 1,
              },
            }}
          >
            {updating ? 'Saving...' : 'Save Date'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReleaseTrackSummary;